/**
 * Rate card editor: the form as typed (strings) → the request body uc-core accepts, with the same
 * checks its DTO and deduction-rule validator make (uc-core src/modules/gate/dto/rate-card.dto.ts,
 * utils/deduction-engine.util.ts), so problems show inline before anything is sent.
 */
import { divRoundHalfUp } from '@/lib/money'
import { inrToPaise } from '../api/mappers'
import { applyDeductionRules, validateDeductionRules, type DeductionRule } from './lotMath'

export type Stream = 'MAIN' | 'CONV'
export type RuleAction = DeductionRule['action']

export interface RuleDraft {
  /** Stable React key for the row. */
  key: string
  from: string
  to: string
  action: RuleAction
  base: string
  perPoint: string
}

export interface RateCardDraft {
  cropId: string
  stream: Stream
  price: string
  effectiveFrom: string
  effectiveTo: string
  rules: RuleDraft[]
}

export type RuleField = 'from' | 'to' | 'base' | 'perPoint'

export interface RateCardProblems {
  cropId?: string
  price?: string
  effectiveFrom?: string
  effectiveTo?: string
  /** Per rule row (same order as `rules`). */
  rules: Array<Partial<Record<RuleField, string>>>
  /** Whole-table problems (overlaps, server-side rule checks). */
  table: string[]
}

export const ACTION_LABEL: Record<RuleAction, string> = {
  ACCEPT: 'Accept',
  DEDUCT: 'Deduct',
  DIVERT: 'Divert',
}

const PRICE_RE = /^\d{1,8}(\.\d{1,2})?$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
/** Percent with at most 2 decimals — the engine works in hundredths of a percent. */
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/

let seq = 0
export const newRuleKey = () => `r${++seq}`

export function emptyRule(action: RuleAction = 'ACCEPT'): RuleDraft {
  return { key: newRuleKey(), from: '', to: '', action, base: '', perPoint: '' }
}

export function emptyRateCardDraft(today: string): RateCardDraft {
  return { cropId: '', stream: 'MAIN', price: '', effectiveFrom: today, effectiveTo: '', rules: [] }
}

const pctText = (n: number | undefined) => (n === undefined || n === null ? '' : String(n))

/** A saved card → editable draft (also used for "copy to a new draft"). */
export function draftFromCard(card: {
  cropId: string | null
  stream: string
  basePricePerKg: string
  effectiveFrom: string
  effectiveTo: string | null
  deductionRules: ReadonlyArray<DeductionRule>
}): RateCardDraft {
  return {
    cropId: card.cropId ?? '',
    stream: card.stream === 'CONV' ? 'CONV' : 'MAIN',
    price: card.basePricePerKg,
    effectiveFrom: card.effectiveFrom.slice(0, 10),
    effectiveTo: card.effectiveTo?.slice(0, 10) ?? '',
    rules: card.deductionRules.map((r) => ({
      key: newRuleKey(),
      from: pctText(r.from_pct),
      to: pctText(r.to_pct),
      action: r.action,
      base: pctText(r.base_pct),
      perPoint: pctText(r.deduct_pct_per_point),
    })),
  }
}

function isRealDate(s: string): boolean {
  if (!DATE_RE.test(s)) return false
  const d = new Date(`${s}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

function pctProblem(text: string, label: string): string | undefined {
  const t = text.trim()
  if (!t) return `${label} is required`
  if (!PCT_RE.test(t)) return `${label}: a number like 12 or 12.5`
  if (Number(t) > 100) return `${label} cannot be over 100`
  return undefined
}

/** Rule rows → the JSON rules the server stores (numbers; base/per point only for Deduct). */
export function rulesFromDraft(rows: ReadonlyArray<RuleDraft>): DeductionRule[] {
  return rows.map((r) => {
    const rule: DeductionRule = {
      kind: 'MOISTURE',
      from_pct: Number(r.from.trim()),
      to_pct: Number(r.to.trim()),
      action: r.action,
    }
    if (r.action === 'DEDUCT') {
      rule.base_pct = Number(r.base.trim())
      rule.deduct_pct_per_point = Number(r.perPoint.trim())
    }
    return rule
  })
}

/** Every problem with the draft; `isValid` when there are none. */
export function validateRateCardDraft(d: RateCardDraft, mode: 'create' | 'edit'): RateCardProblems {
  const p: RateCardProblems = { rules: [], table: [] }
  if (mode === 'create' && !d.cropId) p.cropId = 'Choose the crop'
  const price = d.price.trim()
  if (!price) p.price = 'Enter the price per kg'
  else if (!PRICE_RE.test(price)) p.price = 'Rupees with at most 2 decimals, e.g. 62.00'
  if (!d.effectiveFrom) p.effectiveFrom = 'Choose the first day'
  else if (!isRealDate(d.effectiveFrom)) p.effectiveFrom = 'Not a date'
  if (d.effectiveTo) {
    if (!isRealDate(d.effectiveTo)) p.effectiveTo = 'Not a date'
    else if (!p.effectiveFrom && d.effectiveTo < d.effectiveFrom) p.effectiveTo = 'Ends before it starts'
  }
  let rowsOk = true
  d.rules.forEach((r, i) => {
    const e: Partial<Record<RuleField, string>> = {}
    const from = pctProblem(r.from, 'From %')
    const to = pctProblem(r.to, 'To %')
    if (from) e.from = from
    if (to) e.to = to
    if (!from && !to && Number(r.to) < Number(r.from)) e.to = 'To % must be at least from %'
    if (r.action === 'DEDUCT') {
      const base = pctProblem(r.base, 'Base %')
      if (base) e.base = base
      const pp = r.perPoint.trim()
      if (!pp) e.perPoint = 'Deduct % per point is required'
      else if (!PCT_RE.test(pp)) e.perPoint = 'A number like 1 or 1.5'
    }
    if (Object.keys(e).length) rowsOk = false
    p.rules[i] = e
  })
  // Field checks first; the shared rule validator (same as the server's, plus overlaps) on clean rows.
  if (rowsOk) p.table = validateDeductionRules(rulesFromDraft(d.rules))
  return p
}

export function hasProblems(p: RateCardProblems): boolean {
  return Boolean(
    p.cropId || p.price || p.effectiveFrom || p.effectiveTo || p.table.length || p.rules.some((r) => Object.keys(r).length),
  )
}

export interface CreateRateCardBody {
  cropId: string
  stream: Stream
  basePricePerKg: string
  deductionRules: DeductionRule[]
  effectiveFrom: string
  effectiveTo?: string
}
export interface UpdateRateCardBody {
  basePricePerKg: string
  deductionRules: DeductionRule[]
  effectiveFrom: string
  effectiveTo: string | null
}

/** POST body — crop and stream only on create (the PATCH DTO refuses them). */
export function createBody(d: RateCardDraft): CreateRateCardBody {
  return {
    cropId: d.cropId,
    stream: d.stream,
    basePricePerKg: d.price.trim(),
    deductionRules: rulesFromDraft(d.rules),
    effectiveFrom: d.effectiveFrom,
    ...(d.effectiveTo ? { effectiveTo: d.effectiveTo } : {}),
  }
}
/** PATCH body — an empty end date clears it (open ended). */
export function updateBody(d: RateCardDraft): UpdateRateCardBody {
  return {
    basePricePerKg: d.price.trim(),
    deductionRules: rulesFromDraft(d.rules),
    effectiveFrom: d.effectiveFrom,
    effectiveTo: d.effectiveTo || null,
  }
}

export interface FarmerPreview {
  action: RuleAction
  /** Matched band (1-based) or null when no band covers the reading. */
  band: number | null
  deductionPct: string
  payableG: number
  amountPaise: number
}

/**
 * What a farmer gets for `netG` grams at `moisture` % on this card (the server's engine: deduction
 * in basis points, payable grams rounded once, amount = payable × ₹/kg rounded half up).
 * null when the reading or price cannot be read.
 */
export function previewFarmerGets(
  rules: ReadonlyArray<DeductionRule>,
  price: string,
  moisture: string,
  netG: number,
): FarmerPreview | null {
  const m = moisture.trim()
  if (!m || !PCT_RE.test(m)) return null
  if (!PRICE_RE.test(price.trim())) return null
  const paisePerKg = inrToPaise(price.trim()) ?? 0
  const r = applyDeductionRules(rules, m, BigInt(netG))
  const bp = Number(r.deductionBp)
  return {
    action: r.action,
    band: r.ruleIndex === null ? null : r.ruleIndex + 1,
    deductionPct: `${Math.trunc(bp / 100)}.${String(bp % 100).padStart(2, '0')}`,
    payableG: Number(r.payableGrams),
    amountPaise: Number(divRoundHalfUp(r.payableGrams * BigInt(paisePerKg), 1000n)),
  }
}
